import styles from './UserCommand.module.css'
import {useSessionContext} from '../../contexts/SessionContext'
import {Stack} from '@primer/react'
import type {Command, CompletionUserMessage, UserCommandState, CommandType} from '../../types/session'
import {UserMessageBubble} from './UserMessageBubble'
import pluralize from 'pluralize'
import {isActiveSession} from '../../types/session'
import {useAgentProfile} from '../../contexts/AgentProfileContext'
import {isSourceForCommand, isInjectedMessage} from '../../utils/command-helpers'
import {COMMAND_TYPES} from '../../constants/command-types'

type UserCommandDisplayState = 'queued' | 'in_logs' | 'failed' | 'dismissed'
type DisplayCommand = {
  state: UserCommandDisplayState
  content: string
}

/**
 * Check if a command type should be hidden from the user message display.
 * Prompt responses are displayed inline with the prompt UI, and control
 * commands like mode_switch are not user-visible messages.
 */
function isHiddenCommandType(type?: CommandType): boolean {
  return (
    type === COMMAND_TYPES.ASK_USER_RESPONSE ||
    type === COMMAND_TYPES.ELICITATION_RESPONSE ||
    type === COMMAND_TYPES.PERMISSION_RESPONSE ||
    type === COMMAND_TYPES.PLAN_APPROVAL_RESPONSE ||
    type === COMMAND_TYPES.MODE_SWITCH ||
    type === COMMAND_TYPES.ABORT
  )
}

function stateToDescriptors({state, count}: {state: UserCommandDisplayState; count: number}) {
  const descriptors: Record<
    UserCommandDisplayState,
    {actionText?: string; postScript?: string; containerStateClass?: string}
  > = {
    queued: {actionText: 'Queued for Copilot', containerStateClass: styles.containerMuted},
    failed: {
      actionText: `Unable to submit ${pluralize('message', count)}`,
      containerStateClass: styles.containerMuted,
    },
    in_logs: {},
    dismissed: {
      actionText: `Copilot finished the session before it could pick up ${pluralize('this', count)} ${pluralize(
        'message',
        count,
      )}`,
      containerStateClass: styles.containerMuted,
    },
  }

  return descriptors[state]
}

// inLogs indicates whether we're displaying this command embedded in the logs or above the chat window
function getDisplayState({
  sessionIsActive,
  commandState,
  inLogs,
}: {
  sessionIsActive: boolean
  commandState: UserCommandState
  inLogs: boolean
}): UserCommandDisplayState {
  if (inLogs) {
    return 'in_logs'
  }
  const happyPathStates = ['queued', 'in_progress', 'completed']
  const unhappyPathStates = ['failed', 'dismissed']

  if (!sessionIsActive && happyPathStates.includes(commandState)) {
    // if a session is finished and the command isn't in the logs, something went wrong
    return 'dismissed'
  } else if (happyPathStates.includes(commandState)) {
    return 'queued'
  } else if (unhappyPathStates.includes(commandState)) {
    return commandState as UserCommandDisplayState
  } else {
    throw new Error(`Unexpected command state: ${commandState}`)
  }
}

export function LogCommands({
  rawCommands,
  commandData,
}: {
  rawCommands: CompletionUserMessage[]
  commandData: Command[]
}) {
  const commands: DisplayCommand[] = []
  const {session} = useSessionContext()
  const {interactiveSession} = useAgentProfile()
  const sessionIsActive = isActiveSession(session)
  for (const rawCommand of rawCommands) {
    if (isInjectedMessage(rawCommand)) {
      continue
    }

    const command = findCommandByID(commandData, rawCommand)
    let processedCommand: DisplayCommand | undefined
    if (command) {
      // Skip hidden command types - prompt responses are displayed inline, control commands are not user-visible
      if (isHiddenCommandType(command.type)) {
        continue
      }

      const state = getDisplayState({sessionIsActive, commandState: command.state, inLogs: true})
      processedCommand = {state, content: command.content}
    } else if (interactiveSession) {
      // No matching command - display the raw message content
      const content =
        typeof rawCommand.content === 'string'
          ? rawCommand.content
          : rawCommand.content?.map(c => ('text' in c ? c.text : '')).join('')
      if (content) {
        processedCommand = {state: 'in_logs', content}
      }
    }

    if (processedCommand) {
      commands.push(processedCommand)
    }
  }
  return <ProcessedUserCommands commands={commands} />
}

// Commands that are not yet in the logs
export function ChatCommands({commands, hideStatusText}: {commands: Command[]; hideStatusText?: boolean}) {
  const processedCommands: DisplayCommand[] = []
  const {session} = useSessionContext()
  const sessionIsActive = isActiveSession(session)
  for (const command of commands) {
    if (isHiddenCommandType(command.type)) {
      continue
    }

    const state = hideStatusText
      ? 'in_logs'
      : getDisplayState({sessionIsActive, commandState: command.state, inLogs: false})
    const processedCommand = {state, content: command.content}
    processedCommands.push(processedCommand)
  }
  return <ProcessedUserCommands commands={processedCommands} />
}

export function ProcessedUserCommands({commands}: {commands: DisplayCommand[]}) {
  const commandsByState: Array<{state: UserCommandDisplayState; commands: string[]}> = []
  for (const command of commands) {
    const lastGroup = commandsByState[commandsByState.length - 1]
    if (lastGroup?.state === command.state) {
      lastGroup.commands.push(command.content)
    } else {
      commandsByState.push({state: command.state, commands: [command.content]})
    }
  }
  return (
    <>
      {commandsByState.map((commandGroup, index) => (
        <UserCommandGroup key={index} contents={commandGroup.commands} state={commandGroup.state} />
      ))}
    </>
  )
}

function UserCommandGroup({contents, state}: {contents: string[]; state: UserCommandDisplayState}) {
  const descriptors = stateToDescriptors({state, count: contents.length})
  const actionText = descriptors.actionText
  const containerStateClass = descriptors.containerStateClass
  return (
    <Stack>
      <div className={`mb-2 ${styles.commandContainer} ${containerStateClass ?? ''}`}>
        {actionText && <div className={`mb-2 ${styles.actionText}`}>{actionText}</div>}
        {contents.map((content, index) => (
          <UserMessageBubble key={index} content={content} />
        ))}
      </div>
    </Stack>
  )
}

function findCommandByID(commandData: Command[], logEntry: CompletionUserMessage) {
  const matchingCommand = commandData.find(command => isSourceForCommand(logEntry.source, command))
  if (!matchingCommand) {
    return null // Skip if no matching command is found
  }
  if (typeof logEntry.content !== 'string') {
    return null // Skip non-string content for now
  }

  return matchingCommand
}
